import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import TheoryExamplesRunner from "../../components/TheoryExamplesRunner";

export default function LinearFunctionPreparationTheory() {
  const intro = (
    <div className="space-y-3">
      <p>
        Funkcja liniowa ma wzór <InlineMath math="f(x)=ax+b" />. Jej wykresem jest
        prosta. Liczba <InlineMath math="a" /> jest <strong>współczynnikiem
        kierunkowym</strong>, a <InlineMath math="b=f(0)" /> jest wyrazem wolnym i
        wskazuje punkt przecięcia wykresu z osią <InlineMath math="OY" />.
      </p>
      <ul style={{ paddingLeft: "1.4rem", marginTop: 0 }}>
        <li><InlineMath math="a>0" /> — funkcja jest rosnąca,</li>
        <li><InlineMath math="a<0" /> — funkcja jest malejąca,</li>
        <li><InlineMath math="a=0" /> — funkcja jest stała.</li>
      </ul>
      <p>
        Miejsce zerowe to argument, dla którego <InlineMath math="f(x)=0" />.
        Gdy <InlineMath math="a\ne0" />, rozwiązujemy równanie
        <InlineMath math="ax+b=0" />, więc <InlineMath math="x_0=-\frac{b}{a}" />.
      </p>
      <p>
        Współczynnik kierunkowy prostej przechodzącej przez dwa punkty
        <InlineMath math="A=(x_1,y_1)" /> i <InlineMath math="B=(x_2,y_2)" /> obliczamy ze wzoru:
      </p>
      <BlockMath math={String.raw`a=\frac{y_2-y_1}{x_2-x_1}`} />
      <p>
        Po przesunięciu wykresu funkcji <InlineMath math="f" /> o wektor
        <InlineMath math={String.raw`\vec v=[p,q]`} /> otrzymujemy funkcję
        <InlineMath math="g" /> opisaną wzorem:
      </p>
      <BlockMath math="g(x)=f(x-p)+q" />
      <p>
        Wewnątrz nawiasu znak jest przeciwny: przesunięcie w prawo o
        <InlineMath math="p" /> daje <InlineMath math="x-p" />. Poza funkcją znak
        jest zgodny: przesunięcie w górę o <InlineMath math="q" /> daje
        <InlineMath math="+q" />.
      </p>
    </div>
  );

  const examples = [
    {
      label: "Przykład 1",
      summary: "Współczynniki, monotoniczność i miejsce zerowe",
      description: "Ze wzoru odczytujemy zachowanie prostej i obliczamy jej przecięcie z osią OX.",
      content: (
        <div className="space-y-3">
          <BlockMath math="f(x)=-2x+6" />
          <p>
            Mamy <InlineMath math="a=-2<0" />, więc funkcja jest malejąca, oraz
            <InlineMath math="b=6" />, więc wykres przecina oś <InlineMath math="OY" />
            w punkcie <InlineMath math="(0,6)" />.
          </p>
          <BlockMath math={String.raw`-2x+6=0\quad\Longrightarrow\quad -2x=-6\quad\Longrightarrow\quad x_0=3`} />
        </div>
      ),
    },
    {
      label: "Przykład 2",
      summary: "Wzór prostej przechodzącej przez dwa punkty",
      description: "Najpierw obliczamy a, a następnie podstawiamy punkt i wyznaczamy b.",
      content: (
        <div className="space-y-3">
          <p>Prosta przechodzi przez punkty <InlineMath math="A=(-1,5)" /> i <InlineMath math="B=(2,-1)" />.</p>
          <BlockMath math={String.raw`a=\frac{-1-5}{2-(-1)}=\frac{-6}{3}=-2`} />
          <p>Podstawiamy punkt <InlineMath math="B=(2,-1)" /> do wzoru <InlineMath math="y=ax+b" />:</p>
          <BlockMath math={String.raw`-1=-2\cdot2+b\quad\Longrightarrow\quad b=3`} />
          <BlockMath math="f(x)=-2x+3" />
        </div>
      ),
    },
    {
      label: "Przykład 3",
      summary: "Przesunięcie funkcji liniowej",
      description: "Stosujemy jeden wzór dla przesunięcia poziomego i pionowego.",
      content: (
        <div className="space-y-3">
          <p>
            Niech <InlineMath math="f(x)=2x-1" />. Przesuwamy jej wykres o wektor
            <InlineMath math={String.raw`\vec v=[3,-2]`} />.
          </p>
          <BlockMath math="g(x)=f(x-3)-2" />
          <BlockMath math={String.raw`g(x)=2(x-3)-1-2=2x-9`} />
          <p>
            Współczynnik kierunkowy się nie zmienił, dlatego wykresy funkcji
            <InlineMath math="f" /> i <InlineMath math="g" /> są równoległe.
          </p>
        </div>
      ),
    },
  ];

  return <TheoryExamplesRunner intro={intro} examples={examples} />;
}
